import { z } from 'zod'

// ---------------------------------------------------------------------------
// Health (UI spec §6) — the weekly-review dashboard. Named `health-dashboard`
// rather than `health` to keep this fully separate from `health.ts`'s app
// liveness contract (`GET /api/v1/health`) — same word, two unrelated
// concepts (operator-facing metrics review vs. uptime-monitor payload), and
// keeping them in one file was the kind of collision that gets one of them
// silently overwritten later. Deliberately narrower than the spec's full
// ambition in two places, both because the underlying data model genuinely
// doesn't have the concept yet, not because building it was skipped:
//
// - Autonomy detail's "which threshold triggered" collapses to the single
//   `approval_needed` reason the console already tracks (`AttentionReason`
//   in cadence.ts) — there is no second, third, etc. threshold type
//   anywhere in the data model to break that bucket down further.
// - Policy gaps has no persistent source at all: addendum §I ruled that
//   Rule stays an atomic single-run action in v1, and the Rules-management
//   surface that would generate real policy-gap entries was explicitly not
//   built in Phase 6. `policyGapsSchema` models that as `available: false`
//   with an explanation, per the phase plan's own instruction ("thinner
//   data source... scoped down rather than faked") — not a fake empty list.
// ---------------------------------------------------------------------------

export const weeklyTrendPointSchema = z.object({
  weekStart: z.string(),
  value: z.number(),
  /** Flagged server-side (mean ± 1.5×stddev of the other 11 weeks) — UI spec §6's "small dot." */
  isAnomaly: z.boolean(),
})
export type WeeklyTrendPoint = z.infer<typeof weeklyTrendPointSchema>

const twelveWeekSeriesSchema = z.array(weeklyTrendPointSchema).length(12)

/** The same four Home metrics (UI spec §6), each as its own 12-week series, oldest first. */
export const healthTrendsSchema = z.object({
  runsPerWeek: twelveWeekSeriesSchema,
  autonomyPct: twelveWeekSeriesSchema,
  attentionOpenCount: twelveWeekSeriesSchema,
  verifiedPct: twelveWeekSeriesSchema,
})
export type HealthTrends = z.infer<typeof healthTrendsSchema>

/**
 * The two real exception classes distinct from "required approval" —
 * `ambiguity` (customer identity) and `rule_needed` (unfamiliar catalogue
 * item) from `AttentionReason`, plus `mismatch`, its own verdict rather than
 * a `needs_you` reason but equally "something that took operator time."
 */
/**
 * WIDENED 2026-08-19 during CON-001, from a three-value enum to a free string.
 *
 * The three values were the exception classes the fixture-backed fake could
 * produce. The contract types `hit_exception.by_class[].class` as `z.string()`
 * and real Cadence returns `doesnt_match` — none of the three. A narrow enum
 * here would reject the live response outright, turning a Health screen into a
 * parse error over a value the contract never promised to constrain.
 *
 * Rendering an unfamiliar class verbatim is the right failure mode: the
 * operator sees what Cadence actually said, rather than the console dropping a
 * bucket it did not recognise and silently under-reporting the exceptions.
 */
export const autonomyExceptionClassSchema = z.string()
export type AutonomyExceptionClass = z.infer<typeof autonomyExceptionClassSchema>

export const autonomyDetailSchema = z.object({
  totalCount: z.number(),
  autonomousCount: z.number(),
  requiresApprovalCount: z.number(),
  exceptionCount: z.number(),
  exceptionsByClass: z.array(z.object({ class: autonomyExceptionClassSchema, count: z.number() })),
})
export type AutonomyDetail = z.infer<typeof autonomyDetailSchema>

export const verificationDetailSchema = z.object({
  /** Runs that reached a read-back comparison (confirmed or mismatch) — same scope as Home's `verified`. */
  totalCount: z.number(),
  confirmedCount: z.number(),
  mismatchCount: z.number(),
  /** The single most common differing field across this week's mismatches; `null` when there are none. */
  mismatchTopField: z.string().nullable(),
  /** Always 0 today — nothing in the current data model produces a stalled/pending verification. Modeled honestly for when real Cadence does. */
  notConfirmedYetCount: z.number(),
})
export type VerificationDetail = z.infer<typeof verificationDetailSchema>

/**
 * REPLACED 2026-08-19 during CON-001 — this shape was hiding real data.
 *
 * It used to be `{ available: false, explanation }`, hardcoded, and that was
 * the right call at the time: nothing produced policy gaps, and the phase plan
 * said to scope down rather than fake an empty list. The premise has expired.
 * Real Cadence serves `GET /aggregates/policy-gaps` with actual entries — the
 * first live call returned a pattern covering three runs.
 *
 * A plain list, not a union with the old shape. I tried the union first and it
 * was worse: once the fake also speaks the contract, nothing can produce
 * `available: false` any more, so the branch became unreachable code and a
 * dead arm in the UI. The contract has exactly one way to say this, and an
 * empty list means "Cadence looked and found none" — a real answer, not an
 * absence of one.
 */
export const policyGapSchema = z.object({
  pattern: z.string(),
  count: z.number(),
  exampleRunIds: z.array(z.string()),
  firstSeen: z.string(),
  mostRecent: z.string(),
})
export type PolicyGap = z.infer<typeof policyGapSchema>

export const policyGapsSchema = z.object({ gaps: z.array(policyGapSchema) })
export type PolicyGaps = z.infer<typeof policyGapsSchema>

export const healthDashboardSchema = z.object({
  trends: healthTrendsSchema,
  autonomyDetail: autonomyDetailSchema,
  verificationDetail: verificationDetailSchema,
  policyGaps: policyGapsSchema,
  asOf: z.string(),
})
export type HealthDashboard = z.infer<typeof healthDashboardSchema>
